import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import api from "api/api";

//Props Validations
import PropTypes from "prop-types";

import isEqual from "lodash/isEqual";

import SkewTabs from "components/common/analysisModule/SkewTabs";
import ViewToggle from "components/common/ViewToggle";

import { getGraphLabelProductNameWithTestcodes, getViewKeys, getProductScoreWithNaAndRowSpan, getChartProductsData, getAgreementChartSeries, getDataView, getSortedDataMapping, getAlphabeticalMacroCategories, testResultFlipLabels } from "../../../utils/analysisTable/analysisUtils";
import { getFragranceTTestResultProductHashMap, getColorsAndLetterForIctAgreement, getTestResultByVariable } from "utils/analysisTable/tTestutils";
import SortFinalData from "../../../utils/SortFinalData";

// redux
import { loadProductsVisibility } from "../../../store/features/productsInfoSlice";
import { useSelector, useDispatch } from "react-redux";
import VariableVisibility from "../../common/analysisModule/chart/VariableVisibility";
import GetMacroCategoryOrder from "../../../utils/GetMacroCategoryOrder";
import ScaleAdjuster from "components/common/analysisModule/chart/ScaleAdjuster";
import AnalysisTableBottomLabel from "components/common/analysisModule/AnalysisTableBottomLabel";
import { loadChartScaleResetState } from "store/features/analysisResultsSlice";

// Inline SVG for tooltip (converted to data URL)
const errorOutlineSvg = encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="red">
        <path d="M11 15h2v2h-2zm0-8h2v6h-2zm1-5C6.48 2 2 6.48 2 12s4.48 
        10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 
        0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 
        8-8 8z"/>
    </svg>
`);

function AnalysisChart({ productsSecondaryData = {}, analysisLevelData = {}, settings = {}, tTest = {} }) {
    const { isITMDataShown = false, moduleName, boxAnalysisTypeSelection, boxAnalysisSelection = {}, analysisTypeValue, mappings = [] } = analysisLevelData;
    const { activeTab, setActiveTab } = settings;
    const { tTestLegends, tTestResult, testLabel } = tTest;

    const chartRef = useRef(null);
    const noOfProducts = productsSecondaryData.length;
    const dispatch = useDispatch();

    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const weightToggle = useSelector((state) => state.analysisResultsStore.weightToggle);
    const productsOrder = useSelector((state) => state.productsInfoStore.productsOrder);
    const removedKpiStoreState = useSelector((state) => state.analysisKpiStore[`${moduleName}RemovedKpiList`]);
    const productsTestCodeMapData = useSelector((state) => state.studyDataStore.productTestCodeMap);
    const productColors = useSelector((state) => state.analysisResultsStore.productColors);
    const likertScaleData = useSelector((state) => state.analysisResultsStore.likertScaleData);
    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);
    const itmToggle = useSelector((state) => state.analysisResultsStore.isItmDataShown);
    const chartScaleReset = useSelector((state) => state.analysisResultsStore.chartScaleReset);

    const [draggableColumns, setDraggableColumns] = useState(productsSecondaryData);
    const [dataViewKey, setDataViewKey] = useState("");
    const [box, setBox] = useState(false);

    const [actionStandardKpis, setActionStandardKpis] = useState([]);
    const [asDisplayLabels, setAsDisplayLabels] = useState([]);

    const [variableVisibilityMappings, setVariableVisibilityMappings] = useState([]);

    const customOrderMacroCategory = GetMacroCategoryOrder(productCategoryData?.label?.toLowerCase(), moduleName);

    const [height, setHeight] = useState(230);
    const [yAxisMaxValue, setYAxisMaxValue] = useState(null);

    const [imageHeight, setImageHeight] = useState(300);
    const [inputMinValue, setInputMinValue] = useState(0);
    const [inputMaxValue, setInputMaxValue] = useState(null);
    const [currentZoom, setCurrentZoom] = useState({ start: 0, end: 100 });
    const [testResultFlip, setTestResultFlip] = useState(boxAnalysisTypeSelection.length === 2 ? testResultFlipLabels[0] : boxAnalysisTypeSelection[0]);
    const [tTestLabel, setTTestLabel] = useState("");
    const [topBottomHasTTestResult, setTopBottomHasTTestResult] = useState(false);

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCleansing = process.env?.REACT_APP_SKIN_CLEANSING?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    // signifcance toggle
    const significanceToggle = useSelector((state) => state.analysisResultsStore.significanceToggle);
    const testAnalysisType = useSelector((state) => state.tTestStore.testAnalysisType);

    useEffect(() => {
    if (testLabel) {
        if (testLabel.includes("Top & Bottom Box")) {
            setTTestLabel(testLabel.replace("Top & Bottom Box", testResultFlip));
        } else if (testLabel.includes("Top Box")) {
            setTTestLabel(testLabel.replace("Top Box", testResultFlip));
        } else {
            setTTestLabel(testLabel);
        }
    } else {
        setTTestLabel("");
    }
}, [testResultFlip, testLabel]);

    useEffect(() => {
        setDraggableColumns(SortFinalData(productsSecondaryData, productsOrder));
    }, [productsSecondaryData, productsOrder]);

    useEffect(() => {
        setCurrentZoom({ start: 0, end: 100 });
        setInputMinValue(0);
    }, [chartScaleReset]);

    useEffect(() => {
        const _dataViewKey = getDataView(analysisTypeValue, weightToggle, boxAnalysisTypeSelection);
        const { value } = analysisTypeValue;
        setBox(value === "box_analysis");
        setDataViewKey(_dataViewKey);
        if (chartRef.current) {
            const chartInstance = chartRef.current.getEchartsInstance();
            if (chartInstance) {
                // chartInstance.clear();
            }
        }
    }, [analysisTypeValue, boxAnalysisTypeSelection, weightToggle]);

    const getKpisFromResponse = (response) => {
        const kpis = new Set(
            response
                .map((datum) => {
                    const _kpi = datum?.action_standard_kpis?.map((item) => item?.kpi || []);
                    return _kpi.flat();
                })
                .flat()
        );
        return [...kpis];
    };

    const getActionStandard = useCallback(() => {
        if (selectedStudy.length === 1) {
            const coreDataId = selectedStudy[0]?.id || null;
            if (coreDataId) {
                const url = process.env.REACT_APP_SERVER_PATH + `/action-standard-groups/submit/kpis/?archive=False&core_data_model=${coreDataId}`;
                try {
                    api.get(url)
                        .then(async (res) => {
                            const response = res.data;
                            const kpis = getKpisFromResponse(response);
                            const kpisArray = kpis.map(item => item.backend_variable)
                            setActionStandardKpis([...kpisArray]);
                        })
                        .catch(async (error) => {
                            console.error(error);
                        });
                } catch (error) {
                    console.error(error);
                }
            }
        }
    }, [selectedStudy]);

    useEffect(() => {
        if (selectedStudy.length === 1) {
            getActionStandard();
        }
    }, [getActionStandard, moduleName, selectedStudy]);

    useEffect(() => {
        if (actionStandardKpis.length > 0) {
            const tempDL = [];
            if (mappings.length > 0) {
                mappings.forEach((datum) => {
                    datum?.displayLabels.forEach((ele) => {
                        if (actionStandardKpis.includes(ele.backendVariable)) {
                            tempDL.push(`${ele.displayLabel} (${likertScaleData[ele.backendVariable]})`);
                        }
                    });
                });
                setAsDisplayLabels([...tempDL]);
            }
        }
    }, [mappings, actionStandardKpis, likertScaleData]);

    const getHoverLabel = (bv, key) => {
        return boxAnalysisSelection?.[bv]?.[key]?.label;
    };

    const checkOverlappingScale = (topLabel, bottomLabel) => {
        const topMatch = topLabel.match(/\((\d+)-(\d+)\)/);
        let topArr = [];
        if (topMatch) {
            const start = parseInt(topMatch[1]);
            const end = parseInt(topMatch[2]);

            for (let i = start; i >= end; i--) {
                topArr.push(i);
            }
        }
        const bottomMatch = bottomLabel.match(/\((\d+)-(\d+)\)/);
        let bottomArr = [];
        if (bottomMatch) {
            const start = parseInt(bottomMatch[1]);
            const end = parseInt(bottomMatch[2]);

            for (let i = start; i <= end; i++) {
                bottomArr.push(i);
            }
        }
        const hasCommon = topArr.some((item) => bottomArr.includes(item));

        return hasCommon;
    };

    const getOptionData = useMemo(() => {
        const [key1, key2] = getViewKeys(dataViewKey);
        let catObj = {};
        let macroCategoryOrder = customOrderMacroCategory;
        if (!macroCategoryOrder && moduleName !== "claims") {
            macroCategoryOrder = getAlphabeticalMacroCategories(mappings);
        }
        const sortedData = getSortedDataMapping(mappings, macroCategoryOrder, moduleName, isSkinCleansing || isOralCare);
        let activeKpis = sortedData.flatMap((datum) => datum.displayLabels);
        if (moduleName === "claims") {
            activeKpis = sortedData.flatMap((datum) => datum.displayLabels);
            catObj = { displayLabels: activeKpis };
        } else if (moduleName !== "overall") {
            const activeMacroCategoryKpis = sortedData.find((ele) => ele.macroCategory === activeTab);
            activeKpis = activeMacroCategoryKpis?.displayLabels || [];
            catObj = activeMacroCategoryKpis || {};
        } else {
            catObj = { displayLabels: activeKpis };
        }

        const { productScoreWithNaObj } = getProductScoreWithNaAndRowSpan({ catObj, box, key1, key2, isITMDataShown, moduleName, draggableColumns, noOfProducts, removedKpiStoreState });
        const { _products, _kpis, _rich, _variableVisibilityKpis } = getChartProductsData(productScoreWithNaObj, activeKpis, key1, key2, draggableColumns, dataViewKey, box, noOfProducts, removedKpiStoreState, tTestResult, likertScaleData, moduleName, isITMDataShown, testResultFlip, itmToggle, false, selectedStudy, actionStandardKpis);
        setVariableVisibilityMappings((prev) => (isEqual(prev, _variableVisibilityKpis) ? prev : _variableVisibilityKpis));

        const { _series, _yAxixMaxValue } = getAgreementChartSeries(_products, key1, key2, box, productColors, productColumnVisibility);
        const filteredSeries = _series.filter((s) => draggableColumns.some((p) => p.title === s.name.split(" - ")[0]));
        setYAxisMaxValue(_yAxixMaxValue);
        if (analysisTypeValue.value === "mean") {
            setInputMaxValue(((currentZoom?.end / 100) * _yAxixMaxValue).toFixed(2));
        } else {
            setInputMaxValue(Math.round((currentZoom?.end / 100) * _yAxixMaxValue));
        }

        if (tTestResult.length) {
            const checkTTestResult = tTestResult.some(ele => ele?.colors?.some(color => Object.keys(color).includes("ordinary") && color?.ordinary !== null))
            setTopBottomHasTTestResult(checkTTestResult)
        }

        const _option = {
            tooltip: {
                trigger: "item",
                formatter: (params) => {
                    const { seriesName, dataIndex } = params;
                    const product = seriesName.includes("top") ? seriesName.replace(" - top", "") : seriesName.replace(" - bottom", ""); // get "Product A"
                    const marker = (color) => `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:9px;height:9px;background:${color}"></span>`;
                    if (box && _kpis.length) {
                        const { backendVariable, displayLabel } = _kpis[dataIndex];
                        const findProduct = draggableColumns.find((ele) => ele.title === product);
                        let testResultProductHashMap = {};
                        if (tTestResult.length) {
                            const productCodeCoreDataId = tTestResult.find((res) => res?.backendVariable === backendVariable).coreDataId;
                            const recallTest = getTestResultByVariable(tTestResult, backendVariable, productCodeCoreDataId);
                            testResultProductHashMap = getColorsAndLetterForIctAgreement(testAnalysisType, product, recallTest);
                        }

                        const productName = getGraphLabelProductNameWithTestcodes(selectedStudy, findProduct?.study_reference_title, product, productsTestCodeMapData);
                        const bottomSeries = _series.find((s) => s.name === `${product} - bottom`);
                        const topSeries = _series.find((s) => s.name === `${product} - top`);
                        const bottomValue = bottomSeries?.data[dataIndex];
                        const topValue = topSeries?.data[dataIndex];
                        let text = `<span style="font-weight:bold; font-size:12px">${productName}</b><br/>`;
                        if (dataViewKey.includes("top") && dataViewKey.includes("bottom")) {
                            const topLabel = getHoverLabel(backendVariable, "top_box");
                            const bottomLabel = getHoverLabel(backendVariable, "bottom_box");
                            const isOverlappingScale = checkOverlappingScale(topLabel, bottomLabel);
                            if (isOverlappingScale) {
                                text += `<div style="display: flex; align-items: center;">
                                    <div class="warning-icon"><img src="data:image/svg+xml;utf8,${errorOutlineSvg}" width="12" height="12" /></div>
                                    <div style="font-weight:600; font-size:10px; padding-left:5px;">${displayLabel}</div>
                                </div>

                                <div style="font-weight:400;  font-size:8px;"><i>Caution: Overlapping Scales</i></div><br/>`;
                            } else {
                                text += `<span style="font-weight:600; font-size:10px; padding-left:5px;">${displayLabel}</span><br/><br/>`;
                            }
                        } else {
                            text += `<span style="font-weight:600; font-size:10px; padding-left:5px;">${displayLabel}</span><br/><br/>`;
                        }

                        if (dataViewKey.includes("top")) {
                            const topLabel = getHoverLabel(backendVariable, "top_box");
                            text += `${marker(topSeries?.itemStyle?.color)} `;
                            text += `${topLabel} <span style="float:right; font-weight:bold; padding-left:20px;">${topValue ? topValue + "%" : "NA"} ${testResultProductHashMap?.["label"]?.["top_per"] || ""} ${significanceToggle && testResultProductHashMap?.["label"]?.["top_per"] && testResultProductHashMap?.top_significance ? `[Significance (p-value) ${testResultProductHashMap?.top_significance}]` : ""}</span><br/>`;
                        }

                        if (dataViewKey.includes("bottom")) {
                            const bottomLabel = getHoverLabel(backendVariable, "bottom_box");
                            text += `${marker(bottomSeries?.itemStyle?.color)} `;
                             text += `${bottomLabel} <span style="float:right; font-weight:bold; padding-left:20px;">${bottomValue ? bottomValue + "%" : "NA"} ${testResultProductHashMap?.["label"]?.["bottom_per"] || ""} ${significanceToggle && testResultProductHashMap?.["label"]?.["bottom_per"] && testResultProductHashMap?.bottom_significance ? `[Significance (p-value) ${testResultProductHashMap?.bottom_significance}]` : ""}</span><br/>`;
                        }

                        return text;
                    } else if (!box && _kpis.length) {
                        const { displayLabel, backendVariable } = _kpis[dataIndex];
                        let testResultProductHashMap = {};
                        if (tTestResult.length) {
                            testResultProductHashMap = getFragranceTTestResultProductHashMap(tTestResult);
                        }

                        let text = "";
                        const findProduct = draggableColumns.find((ele) => ele.title === product);
                        const score = _series.find((s) => s.name === product)?.data[dataIndex];
                        const color = _series.find((s) => s.name === product)?.itemStyle.color;
                        // const testResultLabel = testResultProductHashMap?.[backendVariable]?.[product] ?? "";
                        const productName = getGraphLabelProductNameWithTestcodes(selectedStudy, findProduct?.study_reference_title, product, productsTestCodeMapData);
                        text += `${marker(color)} <b>${productName} <span style="float:right; font-weight:bold; padding-left:20px;">${score} ${testResultProductHashMap?.[backendVariable]?.[product]?.label || ""}</span></b><br/>`;
                        text += `<span style="font-weight:600; font-size:12px; padding-left:5px;">${displayLabel}</span><br/>`;
                        if (significanceToggle && testResultProductHashMap?.[backendVariable]?.[product]) {
                            text += `Significance (p-value) ${testResultProductHashMap?.[backendVariable]?.significance_pvalue}<br/><br/>`;
                        } else {
                            text += `<br/>`;
                        }
                        return text;
                    }
                },
            },
            legend: {
                type: "scroll",
                icon: "roundRect",
                itemGap: 16,
                top: -5,
                left: "center",
                width: "90%",
                height: 50,
                orient: "horizontal",
                formatter: function (value) {
                    const product = value.includes("top") ? value.replace(" - top", "") : value.replace(" - bottom", "");
                    const findProduct = draggableColumns.find((ele) => ele.title === product);
                    const productName = getGraphLabelProductNameWithTestcodes(selectedStudy, findProduct?.study_reference_title, product, productsTestCodeMapData);
                    if (box) {
                        const boxProductName = value.includes("top") ? `${productName} - top` : `${productName} - bottom`;
                        return `${boxProductName} (n=${findProduct?.sampleSize})`;
                    } else {
                        return `${productName} (n=${findProduct?.sampleSize})`;
                    }
                },
                selected: productsSecondaryData.reduce((acc, product) => {
                    const visibilityKey = `checked--${product.title.split(" ").join("_")}__${product.coreDataId}`;
                    if (box) {
                        const key1Arr = key1.split("_");
                        const title1 = `${product.title} - ${key1Arr[0]}`;
                        if (key2) {
                            const title2 = `${product.title} - bottom`;
                            acc[title2] = !productColumnVisibility[visibilityKey];
                        }
                        acc[title1] = !productColumnVisibility[visibilityKey];
                    } else {
                        acc[product.title] = !productColumnVisibility[visibilityKey];
                    }
                    return acc;
                }, {}),
            },
            grid: {
                show: true,
                containLabel: true,
                tooltip: {
                    show: true,
                },
                left: "10%",
                right: "5%",
                top: "12%",
            },
            xAxis: {
                type: "category",
                data: _kpis.map((ele) => ele.displayLabel),
                axisLabel: {
                    rotate: 90,
                    align: "right",
                    width: 400,
                    padding: [1, 5],
                    fontFamily: "poppinsregular",
                    formatter: function (value, index) {
                        const finaltxt = asDisplayLabels?.includes(value) && selectedStudy.length === 1 ? `{${index}| ${value}*}` : `{${index}| ${value}}`;
                        return finaltxt;
                    },
                    rich: _rich,
                },
            },
            yAxis: {
                type: "value",
                max: _yAxixMaxValue,
            },
            series: filteredSeries,
            dataZoom: [
                {
                    type: "slider",
                    yAxisIndex: 0,
                    orient: "vertical",
                    width: 15,
                    left: "6%",
                    filterMode: "none",
                    start: currentZoom.start,
                    end: currentZoom.end,
                    rangeMode: ["value", "value"],
                    realtime: false,
                },
                {
                    type: "inside",
                    yAxisIndex: 0,
                    start: currentZoom.start,
                    end: currentZoom.end,
                    rangeMode: ["value", "value"],
                },
            ],
        };
        const _labelMaxLen = _option?.dataset?.source?.[0]?.reduce((max, current) => Math.max(max, current?.length), 0) || 150;
        const _height = 230 + (_labelMaxLen <= 150 ? 150 * 4.15 : _labelMaxLen * 4.15);
        setHeight(_height);

        return _option;
    }, [
        activeTab,
        analysisTypeValue,
        box,
        currentZoom.start,
        currentZoom.end,
        customOrderMacroCategory,
        dataViewKey,
        draggableColumns,
        isITMDataShown,
        isSkinCleansing,
        isOralCare,
        likertScaleData,
        mappings,
        moduleName,
        noOfProducts,
        productColors,
        productsTestCodeMapData,
        removedKpiStoreState,
        selectedStudy,
        tTestResult,
        testResultFlip,
        significanceToggle,
        productColumnVisibility,
        actionStandardKpis,
        asDisplayLabels,
    ]);

    const handleInputChange = (e, type) => {
        if (type === "max") {
            if (e.target.value <= yAxisMaxValue && e.target.value >= 0) {
                const updatedValue = (parseFloat(e.target.value) * 100) / yAxisMaxValue;
                setInputMaxValue(e.target.value);
                setCurrentZoom({ start: currentZoom.start, end: updatedValue });
            }
        } else if (e.target.value >= 0 && e.target.value <= yAxisMaxValue) {
            const updatedValue = (parseFloat(e.target.value) * 100) / yAxisMaxValue;
            setInputMinValue(e.target.value);
            setCurrentZoom({ start: updatedValue, end: currentZoom.end });
        }
        dispatch(loadChartScaleResetState(true));
    };

    const getOnlyImageHeight = useCallback((height) => {
        setImageHeight(height + 120);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (chartRef.current) {
                const chartInstance = chartRef.current.getEchartsInstance();
                const dom = chartInstance.getDom();

                // The ECharts DOM may not include the outer divs you're selecting
                // Go directly to SVG > g > path
                const pathElement = dom.querySelector("svg > g > path");

                if (pathElement) {
                    const bbox = pathElement.getBBox(); // Get the bounding box of the path element
                    getOnlyImageHeight(bbox.height);
                } else {
                    setImageHeight(height);
                }
            }
        }, 0); // Delay to allow chart to fully render

        // Cleanup the timeout
        return () => clearTimeout(timer);
    }, [activeTab, removedKpiStoreState, box, chartRef, height, getOnlyImageHeight]);

    const handleDataZoom = useCallback(
        (params) => {
            const zoomData = params.batch ? params.batch[0] : params;
            if (zoomData?.start !== undefined && zoomData?.end !== undefined) {
                const newMin = ((zoomData?.start / 100) * yAxisMaxValue).toFixed(2);
                const newMax = ((zoomData?.end / 100) * yAxisMaxValue).toFixed(2);
                if (analysisTypeValue.value === "mean") {
                    setInputMinValue(newMin);
                    setInputMaxValue(newMax);
                    setCurrentZoom({ start: zoomData.start, end: zoomData.end });
                } else {
                    setInputMinValue(Math.round(newMin));
                    setInputMaxValue(Math.round(newMax));
                    setCurrentZoom({ start: Math.round(zoomData.start), end: Math.round(zoomData.end) });
                }
            }
            dispatch(loadChartScaleResetState(true));
        },
        [yAxisMaxValue, analysisTypeValue]
    );

    const updateValuesPosition = () => {
        const textElements = document.querySelectorAll("text");

        textElements.forEach((element) => {
            const value = parseFloat(element.textContent);

            if (value < 30) {
                // Get the current transform matrix as a string
                const matrix = element.getAttribute("transform");

                if (matrix && matrix.startsWith("matrix(")) {
                    // Extract the matrix values
                    const matrixValues = matrix.slice(7, -1).split(",").map(Number);
                    matrixValues[5] -= 44;

                    const newMatrix = `matrix(${matrixValues.join(",")})`;
                    // set the new matrix to transform attribute
                    element.setAttribute("transform", newMatrix);
                    element.style.fill = "#333";
                }
            }
        });
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            updateValuesPosition();
        }, 1500);

        return () => clearTimeout(timeout);
    }, [mappings, currentZoom, inputMaxValue, inputMaxValue, dataViewKey]);

    const handleLegendSelectChanged = (params) => {
        const productTitle = params.name;
        const titleArr = params.name.split(" - ");
        const isHidden = !params.selected[productTitle];
        const index = productsSecondaryData.findIndex((product) => product.title === titleArr[0]);
        if (index !== -1) {
            const product = productsSecondaryData.find((ele) => ele.title === titleArr[0]);
            const visibilityKey = `checked--${product.title.split("/").join("/").trim().split(" ").join("_")}__${product.coreDataId}`;
            // Update the productsVisibility store
            const updatedVisibility = {
                ...productColumnVisibility,
                [visibilityKey]: isHidden,
            };
            dispatch(loadProductsVisibility(updatedVisibility));
        }
    };

    return (
        <div
            style={{
                position: "relative",
                marginBottom: "40px",
            }}
        >
            {!isITMDataShown ? (
                <VariableVisibility
                    mappings={variableVisibilityMappings}
                    moduleName={moduleName}
                />
            ) : (
                <div
                    style={{
                        height: "30px",
                    }}
                ></div>
            )}
            <ScaleAdjuster
                settings={{
                    isBoxAnalysis: box,
                    imageHeight: imageHeight,
                    inputMinValue: inputMinValue,
                    inputMaxValue: inputMaxValue,
                    yAxisMaxValue: yAxisMaxValue,
                    handleInputChange: handleInputChange,
                }}
            />
            <div>
                <ReactECharts
                    ref={chartRef}
                    option={getOptionData}
                    style={{
                        minHeight: `${height}px`,
                        height: "100%",
                    }}
                    opts={{ renderer: "svg" }}
                    onEvents={{
                        dataZoom: handleDataZoom,
                        legendSelectChanged: handleLegendSelectChanged,
                    }}
                    notMerge={true}
                    replaceMerge={["series"]}
                />
            </div>
            {box && tTestResult.length !== 0 && topBottomHasTTestResult && (
                <div
                    style={{
                        width: 300,
                        marginBottom: 5,
                        display: "flex",
                        justifyContent: "space-between",
                        border: "1px dotted #005eef",
                        padding: "10px",
                        alignItems: "center",
                    }}
                >
                    <div style={{ width: "150px", fontSize: "13px" }}>Z-Test Result Type:</div>
                    <ViewToggle
                        value={testResultFlip}
                        setValue={setTestResultFlip}
                        labels={testResultFlipLabels}
                        booleanComponent={false}
                    />
                </div>
            )}
            {!["overall", "claims"].includes(moduleName) && (
                <SkewTabs
                    tableData={{
                        productsSecondaryDataState: productsSecondaryData,
                    }}
                    settings={{
                        activeTab: activeTab,
                        setActiveTab: setActiveTab,
                    }}
                    analysisView={{
                        moduleName: moduleName,
                        mappings: mappings,
                        analysisTypeValue: analysisTypeValue,
                        boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                    }}
                />
            )}
            <p
                style={{
                    color: "#495057",
                    fontWeight: 600,
                    fontSize: 12,
                    margin: 0,
                    textAlign: "right",
                }}
            >
                {"Agreement Likert scale size is indicated in brackets () next to each variable"}
            </p>
            <AnalysisTableBottomLabel
                tTest={{
                    tTestLegends: tTestLegends,
                    testLabel: tTestLabel,
                }}
                analysisView={{
                    moduleName: moduleName,
                }}
            />
        </div>
    );
}

AnalysisChart.propTypes = {
    productsSecondaryData: PropTypes.array,
    analysisLevelData: PropTypes.object,
    settings: PropTypes.object,
    tTest: PropTypes.object,
};

export default AnalysisChart;
